import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Icon from 'components/Icon';
import { decisaoDeConsentimento, registrarConsentimento } from 'services/analytics';
import { ehNativo } from 'services/plataforma';

/**
 * A pergunta sobre medição, no aplicativo.
 *
 * <h2>No Android ela não aparece mais</h2>
 * Lá a medição de telas passou a ser opt-out: `decisaoDeConsentimento()` devolve ACEITO para quem
 * nunca decidiu, e esta tela só abre diante de null, então simplesmente não abre. Ela fica no código
 * para outra plataforma nativa (iOS) que venha a precisar perguntar. O opt-out continua em Meu
 * cadastro › Minha Conta.
 *
 * <h2>Por que não o CookieBanner</h2>
 * Aquela faixa fala de **cookies de navegação** e do Google Analytics do site. Dentro do app não há
 * navegador nem terceiro gravando cookie, e ela apareceria descrevendo uma coisa que não está
 * acontecendo — pedindo permissão sobre nada, por cima da primeira tela. Por isso ele é suprimido no
 * build nativo desde o R3b, e por isso o app simplesmente não era medido: sem ninguém para perguntar,
 * a decisão nunca existia.
 *
 * Esta tela é a mesma pergunta com o texto certo: o que o app mede é uso de telas, dentro do app.
 *
 * <h2>Recusar não custa nada</h2>
 * Os dois botões fecham a tela e liberam o app por inteiro. Isso não é cortesia: é requisito da Play
 * Store, que recusa app onde negar consentimento retira funcionalidade. E é a diferença entre
 * perguntar e avisar.
 *
 * A recusa é gravada, não é "ainda não respondeu" — a tela não volta a aparecer. Quem mudar de ideia
 * encontra o controle em Meu cadastro › Minha Conta, nos dois sentidos.
 *
 * <h2>Onde ela aparece</h2>
 * Na abertura, antes de qualquer login: medição é preferência do APARELHO, não da conta. Amarrá-la
 * ao cadastro deixaria de fora justamente quem só olha e vai embora — que é o comportamento que mais
 * interessa medir — e faria a pergunta reaparecer a cada troca de usuário no mesmo aparelho.
 */
const ConsentimentoApp = () => {
    const [perguntar, setPerguntar] = useState(false);

    useEffect(() => {
        // Só no app, e só para quem nunca decidiu. `decisaoDeConsentimento()` distingue "recusou" de
        // "nunca viu" -- usar `temConsentimento()` aqui faria a tela voltar a cada abertura para
        // quem disse não.
        if (ehNativo() && decisaoDeConsentimento() === null) {
            setPerguntar(true);
        }
    }, []);

    const decidir = (aceitou) => {
        registrarConsentimento(aceitou);
        setPerguntar(false);
    };

    if (!perguntar) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="consentimento-titulo"
            className="fixed inset-0 z-[1100] flex flex-col justify-end bg-dark/70"
        >
            {/* Folha por baixo, e não caixa centralizada: os botões nascem ao alcance do polegar, que
                é onde uma decisão de uma toque só deve estar num celular. O padding de baixo soma o
                inset da área segura -- sem ele, num aparelho com barra de gestos, o botão fica
                debaixo dela. Via --safe-bottom, e não env() direto: é a variável que sabe ler o
                inset que o Capacitor injeta (ver styles/index.css). */}
            <div className="rounded-t-2xl bg-surface px-6 pt-6 pb-[calc(1.5rem+var(--safe-bottom))] shadow-2xl">
                <div className="mx-auto w-full max-w-[560px]">
                    <div className="mb-4 flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10">
                            <Icon name="visibility" className="h-5 w-5 text-accent" />
                        </span>
                        <h2 id="consentimento-titulo" className="t-bloco">
                            Podemos medir como você usa o app?
                        </h2>
                    </div>

                    <p className="t-apoio mb-2 text-muted">
                        Se você permitir, registramos quais telas são abertas para entender o que
                        precisa melhorar. Não registramos o que você digita, e esses dados não são
                        usados para identificar você.
                    </p>
                    <p className="t-apoio mb-6 text-muted">
                        O app funciona igual de qualquer forma — e você pode mudar essa escolha
                        quando quiser, em <strong className="text-dark">Meu cadastro › Minha Conta</strong>.{' '}
                        <Link
                            to="/politica-de-privacidade"
                            className="font-semibold text-accent underline underline-offset-[3px]"
                        >
                            Política de Privacidade
                        </Link>
                    </p>

                    {/* Empilhados e de largura cheia: os dois recebem o mesmo alvo de toque, e nenhum
                        fica espremido na borda da tela onde o polegar erra.

                        Nenhum dos dois é "btn-outline" ou texto solto: a diferença entre eles é de
                        ênfase (primary x ghost), não de disponibilidade. Recusar continua sendo um
                        botão de verdade, do mesmo tamanho -- dar a escolha no texto e retirá-la no
                        desenho é a forma mais comum de fingir consentimento. */}
                    <div className="flex flex-col gap-3">
                        <button
                            type="button"
                            onClick={() => decidir(true)}
                            className="btn btn-primary w-full"
                        >
                            Permitir
                        </button>
                        <button
                            type="button"
                            onClick={() => decidir(false)}
                            className="btn btn-ghost w-full"
                        >
                            Não permitir
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsentimentoApp;
